import { rd } from '@passionware/monads';
import { createReadWriteApiStore } from 'v5/platform/react/readWriteApiStore';
import { createLocalStorageApi } from 'v5/platform/ts/localStorageApi';
import {
  ProductionSearchQuery,
  productionQueryUtils,
  productionSearchQuerySchema
} from 'v5/api/production/production.api';
import {
  ResourceSearchQuery,
  resourceQueryUtils,
  resourceSearchQuerySchema
} from 'v5/api/resource/resource.api';
import { z } from 'zod';
import {
  EntityListLayoutPreference,
  ProductionsListViewPreference,
  ShortcutMapping,
  UserPreferencesService
} from './UserPreferencesService';

function parseCallsheetProductionQuery(value: unknown): ProductionSearchQuery {
  // The callsheet picker has its own first-open default state.
  if (value == null) {
    return productionQueryUtils.ofDefault();
  }

  return productionSearchQuerySchema.parse(value);
}

function parseResourcingAssignResourceQuery(
  value: unknown
): ResourceSearchQuery {
  if (value == null) {
    return resourceQueryUtils.ofDefault();
  }

  return resourceSearchQuerySchema.parse(value) as ResourceSearchQuery;
}

const productionsListViewPreferenceSchema = z.object({
  showResourcingBudgetColumn: z.boolean(),
  budgetChartMode: z.enum(['vsPlanned', 'vsBudget']).optional()
});

function parseProductionsListView(
  value: unknown,
  defaults: ProductionsListViewPreference
): ProductionsListViewPreference {
  if (value == null) {
    return defaults;
  }

  const parsed = productionsListViewPreferenceSchema.safeParse(value);
  if (!parsed.success) {
    return defaults;
  }

  return {
    ...defaults,
    ...parsed.data,
    budgetChartMode: parsed.data.budgetChartMode ?? defaults.budgetChartMode
  };
}

export function createUserPreferencesService(): UserPreferencesService {
  const defaultEntityListLayout: EntityListLayoutPreference = {
    viewMode: 'list',
    tileMinWidthPx: 280
  };
  const useEntityListLayout = createReadWriteApiStore(
    createLocalStorageApi(
      'entity-list-layout',
      z.record(
        z.string(),
        z.object({
          viewMode: z.enum(['grid', 'list']),
          tileMinWidthPx: z.number()
        })
      ).parse,
      {}
    )
  );
  const callsheetsProductionQuery = createReadWriteApiStore(
    createLocalStorageApi(
      'callsheets-production-query',
      parseCallsheetProductionQuery,
      productionQueryUtils.ofDefault()
    )
  );
  const resourcingAssignResourceQuery = createReadWriteApiStore(
    createLocalStorageApi(
      'resourcing-assign-resource-query',
      parseResourcingAssignResourceQuery,
      resourceQueryUtils.ofDefault()
    )
  );
  const defaultProductionsListView: ProductionsListViewPreference = {
    showResourcingBudgetColumn: false,
    budgetChartMode: 'vsPlanned'
  };
  const productionsListView = createReadWriteApiStore(
    createLocalStorageApi(
      'productions-list-view',
      value => parseProductionsListView(value, defaultProductionsListView),
      defaultProductionsListView
    )
  );
  const useEntityTableColumns = createReadWriteApiStore(
    createLocalStorageApi(
      'entity-table-columns',
      z.record(z.string(), z.array(z.string())).parse,
      {}
    )
  );
  return {
    useShortcuts: () => {
      // TODO: Load from localStorage or backend in the future
      const shortcuts: ShortcutMapping = {
        create: {
          production: 'mod+alt+p',
          shortlist: 'mod+alt+s',
          booking: 'mod+alt+b',
          callsheet: 'mod+alt+c',
          callout: 'mod+alt+o',
          resource: 'mod+alt+r'
        }
      };

      return rd.of(shortcuts);
    },

    forEntityListLayout: key => ({
      useValue: () =>
        rd.map(
          useEntityListLayout.useValue(),
          data => data[key ?? 'default'] ?? defaultEntityListLayout
        ),
      ensureValue: async () =>
        rd.map(
          await useEntityListLayout.ensureValue(),
          data => data[key ?? 'default'] ?? defaultEntityListLayout
        ),
      setValue: async value =>
        useEntityListLayout.setValue({
          ...rd.getOrThrow(
            await useEntityListLayout.ensureValue(),
            'Attempted to set value when preference is not loaded'
          ),
          [key ?? 'default']: value
        })
    }),

    forEntityTableColumns: scopeKey => ({
      useValue: () =>
        rd.map(useEntityTableColumns.useValue(), data => data[scopeKey]),
      ensureValue: async () =>
        rd.map(
          await useEntityTableColumns.ensureValue(),
          data => data[scopeKey]
        ),
      setValue: async value => {
        const data = rd.getOrThrow(
          await useEntityTableColumns.ensureValue(),
          'Attempted to set entity table columns when preference is not loaded'
        );
        if (value === undefined) {
          const { [scopeKey]: _removed, ...rest } = data;
          useEntityTableColumns.setValue(rest);
          return;
        }
        useEntityTableColumns.setValue({
          ...data,
          [scopeKey]: value
        });
      }
    }),
    productionsListView,
    callsheetsProductionQuery,
    resourcingAssignResourceQuery
  };
}
